import { get } from "@src/appV2/api/parsedApi";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import {
  useGetWorkerDetailsAtWorkplace,
  WorkerCategory,
} from "@src/appV2/Professionals/api/useGetWorkerDetailsAtWorkplace";
import {
  buildShiftInvitesDateRangeFilter,
  serializeShiftInvitesQueryParams,
  shiftInvitesDateRangeFilterSchema,
} from "@src/appV2/ShiftInvites/api/shiftInvitesDateRangeQuery";
import { SHIFT_INVITES_URL } from "@src/appV2/ShiftInvites/constants";
import { ShiftInviteStatus } from "@src/appV2/ShiftInvites/type";
import { useQuery, useQueryClient } from "@tanstack/react-query";
// eslint-disable-next-line no-restricted-imports -- consistent with surrounding calendar code
import { format } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- @clipboard-health/date-time doesn't export zonedTimeToUtc equivalent
import { zonedTimeToUtc } from "date-fns-tz";
import { useCallback, useMemo } from "react";
import { z } from "zod";

import { getShiftSlotKey, type PendingInviteInfo } from "../utils/mapShiftToCard";

/**
 * Stable empty map to avoid creating new references on every render.
 * Used as fallback when query data is not yet available.
 */
const EMPTY_PENDING_INVITES_MAP = new Map<string, PendingInviteInfo[]>();

const pendingInviteAgentSchema = z.object({
  name: z.string(),
  userId: z.string(),
  profileImageUrl: z.string().optional(),
});

const pendingInviteShiftDetailsSchema = z.object({
  start: z.string(),
  end: z.string(),
  agentReq: z.string(),
  name: z.string(),
  tmz: z.string().optional(),
});

const pendingInviteAttributesSchema = z.object({
  workerId: z.string(),
  workplaceId: z.string(),
  shiftId: z.string().optional(),
  shiftDetails: pendingInviteShiftDetailsSchema,
  expiresAt: z.string(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  status: z.nativeEnum(ShiftInviteStatus).optional(),
  // agent might not be included in the list response, make it optional
  agent: pendingInviteAgentSchema.optional(),
  postShiftToMarketplace: z.boolean(),
  isPrnInvite: z.boolean().optional(),
});

const pendingInviteItemSchema = z.object({
  attributes: pendingInviteAttributesSchema,
  id: z.string(),
  type: z.string(),
});

const pendingInvitesResponseSchema = z.object({
  data: z.array(pendingInviteItemSchema),
});

type PendingInviteItem = z.infer<typeof pendingInviteItemSchema>;

const queryParamsSchema = shiftInvitesDateRangeFilterSchema.extend({
  sort: z.string(),
});

interface UseGetPendingInvitesParams {
  workplaceId: string;
  day: Date;
  timezone: string;
}

interface UseGetPendingInvitesOptions {
  enabled?: boolean;
}

function getDayBoundaryFilters(day: Date, timezone: string): { start: string; end: string } {
  const dateString = format(day, "yyyy-MM-dd");
  return {
    start: zonedTimeToUtc(`${dateString}T00:00:00`, timezone).toISOString(),
    end: zonedTimeToUtc(`${dateString}T23:59:59.999`, timezone).toISOString(),
  };
}

function buildPendingInvitesQueryKey(
  workplaceId: string,
  day: Date,
  timezone: string
): readonly unknown[] {
  const { start, end } = getDayBoundaryFilters(day, timezone);
  return [SHIFT_INVITES_URL, workplaceId, start, end] as const;
}

async function fetchPendingInvites(
  workplaceId: string,
  day: Date,
  timezone: string
): Promise<PendingInviteItem[]> {
  const { start: startOfDayFilter, end: endOfDayFilter } = getDayBoundaryFilters(day, timezone);

  const response = await get({
    url: SHIFT_INVITES_URL,
    queryParams: {
      ...buildShiftInvitesDateRangeFilter({
        workplaceId,
        start: startOfDayFilter,
        end: endOfDayFilter,
        status: [
          ShiftInviteStatus.PENDING,
          ShiftInviteStatus.EXPIRED,
          ShiftInviteStatus.DECLINED,
          ShiftInviteStatus.ACCEPTED,
        ],
      }),
      sort: "-expiresAt",
    },
    queryParamsSchema,
    responseSchema: pendingInvitesResponseSchema,
    paramsSerializer: serializeShiftInvitesQueryParams,
  });

  return response.data.data;
}

function transformToPendingInviteInfo(
  invite: PendingInviteItem,
  workerProfilesMap: Map<string, string>
): PendingInviteInfo {
  const {
    agent,
    workerId,
    expiresAt,
    createdAt,
    updatedAt,
    postShiftToMarketplace,
    shiftDetails,
    status,
    shiftId,
  } = invite.attributes;

  // Try to get profile image from: 1) fetched worker details, 2) agent in invite response
  const profileImageUrl = workerProfilesMap.get(workerId) ?? agent?.profileImageUrl;

  return {
    inviteId: invite.id,
    shiftId,
    workerName: agent?.name ?? "Invited Professional",
    workerId: agent?.userId ?? workerId,
    workerAvatarUrl: profileImageUrl,
    expiresAt,
    createdAt,
    updatedAt,
    postShiftToMarketplace,
    status,
    shiftDetails: {
      start: shiftDetails.start,
      end: shiftDetails.end,
      agentReq: shiftDetails.agentReq,
      timeSlot: shiftDetails.name,
      tmz: shiftDetails.tmz,
    },
  };
}

/**
 * Builds a map of slot keys to arrays of pending invites.
 * Multiple invites can exist for the same time slot (e.g., 5 shifts posted, 2 invited).
 */
function buildPendingInvitesMap(
  invites: PendingInviteItem[],
  workerProfilesMap: Map<string, string>
): Map<string, PendingInviteInfo[]> {
  const map = new Map<string, PendingInviteInfo[]>();

  for (const invite of invites) {
    const { start, end, agentReq } = invite.attributes.shiftDetails;
    const slotKey = getShiftSlotKey(start, end, agentReq);

    const existingInvites = map.get(slotKey) ?? [];
    existingInvites.push(transformToPendingInviteInfo(invite, workerProfilesMap));
    map.set(slotKey, existingInvites);
  }

  return map;
}

/**
 * Hook to fetch pending shift invites for a specific day.
 * Also fetches worker profile images for the invited workers.
 * Returns a map of slot keys to pending invite info for efficient lookup.
 */
export function useGetPendingInvites(
  params: UseGetPendingInvitesParams,
  options: UseGetPendingInvitesOptions = {}
) {
  const { workplaceId, day, timezone } = params;
  const { enabled = true } = options;

  // Fetch both pending and expired invites in a single query
  const invitesQuery = useQuery({
    queryKey: buildPendingInvitesQueryKey(workplaceId, day, timezone),
    queryFn: async () => fetchPendingInvites(workplaceId, day, timezone),
    enabled: enabled && Boolean(workplaceId) && Boolean(timezone),
    staleTime: 30_000,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_PENDING_INVITES_FAILURE,
    },
  });

  // Skip PRN sibling invites — those render as a single posting card via
  // `useActivePrnShiftPostings`, not as N "Invite sent" cards.
  const allInvites = (invitesQuery.data ?? []).filter(({ attributes }) => !attributes.isPrnInvite);
  const pendingInvites = allInvites.filter(
    ({ attributes }) => attributes.status === ShiftInviteStatus.PENDING
  );
  const expiredInvitesData = allInvites.filter(
    ({ attributes }) =>
      (attributes.status === ShiftInviteStatus.EXPIRED ||
        attributes.status === ShiftInviteStatus.DECLINED) &&
      !attributes.postShiftToMarketplace
  );
  // Invites that were declined/expired and created a new open shift (postShiftToMarketplace=true).
  // The invite's shiftId points to the newly created open shift.
  const declinedWithNewShiftData = allInvites.filter(
    ({ attributes }) =>
      (attributes.status === ShiftInviteStatus.EXPIRED ||
        attributes.status === ShiftInviteStatus.DECLINED) &&
      attributes.postShiftToMarketplace &&
      attributes.shiftId !== undefined
  );
  // Accepted invites — the invite's shiftId points to the filled shift it booked.
  const acceptedWithShiftData = allInvites.filter(
    ({ attributes }) =>
      attributes.status === ShiftInviteStatus.ACCEPTED && attributes.shiftId !== undefined
  );

  // Extract unique worker IDs from both pending and expired invites
  const workerIds = useMemo(
    () =>
      // eslint-disable-next-line unicorn/prefer-spread
      Array.from(new Set((invitesQuery.data ?? []).map((invite) => invite.attributes.workerId))),
    [invitesQuery.data]
  );

  // Third query: fetch worker details including profile images
  const workersQuery = useGetWorkerDetailsAtWorkplace(
    {
      category: WorkerCategory.ALL_WORKERS,
      facilityId: workplaceId,
      workerIds,
      pageSize: workerIds.length || 1,
    },
    {
      enabled: enabled && workerIds.length > 0,
    }
  );

  // Build worker profiles map from fetched worker details
  const workerProfilesMap = useMemo(() => {
    const map = new Map<string, string>();
    if (!workersQuery.data?.workers) {
      return map;
    }
    for (const worker of workersQuery.data.workers) {
      if (worker.profileImageUrl) {
        map.set(worker.userId, worker.profileImageUrl);
      }
    }
    return map;
  }, [workersQuery.data?.workers]);

  // Combine pending invites with worker profile images
  const pendingInvitesMap = useMemo(() => {
    if (pendingInvites.length === 0) {
      return EMPTY_PENDING_INVITES_MAP;
    }
    return buildPendingInvitesMap(pendingInvites, workerProfilesMap);
  }, [pendingInvites, workerProfilesMap]);

  // Build expired invites array for display in expandable section
  const expiredInvites = useMemo(
    () =>
      expiredInvitesData.map((invite) => transformToPendingInviteInfo(invite, workerProfilesMap)),
    [expiredInvitesData, workerProfilesMap]
  );

  // Map shiftId → PendingInviteInfo for invites that declined/expired and posted a new open shift.
  // Used by the daily view to annotate the new open shift with the original invitee as previousWorker.
  const declinedInvitesByShiftId = useMemo(() => {
    const map = new Map<string, PendingInviteInfo>();
    for (const invite of declinedWithNewShiftData) {
      const { shiftId } = invite.attributes;
      if (shiftId) {
        map.set(shiftId, transformToPendingInviteInfo(invite, workerProfilesMap));
      }
    }
    return map;
  }, [declinedWithNewShiftData, workerProfilesMap]);

  // Map shiftId → PendingInviteInfo for terminal invites that own a shift: an
  // accepted invite (→ its filled shift) or a declined/expired invite that
  // re-posted to the marketplace (→ the new open shift). Used to render the
  // invite-history timeline in the shift-details panel. If a shiftId ever has
  // multiple terminal invites, the most recently updated one wins.
  const invitesByShiftId = useMemo(() => {
    const map = new Map<string, PendingInviteInfo>();
    const candidates = [...acceptedWithShiftData, ...declinedWithNewShiftData];
    for (const invite of candidates) {
      const { shiftId } = invite.attributes;
      if (!shiftId) {
        continue;
      }
      const next = transformToPendingInviteInfo(invite, workerProfilesMap);
      const existing = map.get(shiftId);
      const nextUpdatedAt = next.updatedAt ? new Date(next.updatedAt).getTime() : 0;
      const existingUpdatedAt = existing?.updatedAt ? new Date(existing.updatedAt).getTime() : 0;
      if (!existing || nextUpdatedAt > existingUpdatedAt) {
        map.set(shiftId, next);
      }
    }
    return map;
  }, [acceptedWithShiftData, declinedWithNewShiftData, workerProfilesMap]);

  // Profile images are an enrichment, not core invite data. Gating on
  // `workersQuery.isLoading` would flip this hook to "loading" every time the
  // invite set changes (e.g. immediately after sending a new invite), causing
  // the daily view to unmount its scroll container and snap back to the AM
  // slot. The pending invite list already has `workerName`/`workerId` and
  // renders fine while avatars trickle in. (ORB-1920)
  return {
    isLoading: invitesQuery.isLoading,
    pendingInvitesMap,
    expiredInvites,
    declinedInvitesByShiftId,
    invitesByShiftId,
  };
}

/**
 * Returns a stable prefetch function for pending invites on a given day.
 * Call it with a target date to warm the cache before the user navigates there.
 */
export function usePrefetchPendingInvites() {
  const queryClient = useQueryClient();

  return useCallback(
    async (workplaceId: string, day: Date, timezone: string) => {
      await queryClient.prefetchQuery({
        queryKey: buildPendingInvitesQueryKey(workplaceId, day, timezone),
        queryFn: async () => fetchPendingInvites(workplaceId, day, timezone),
        staleTime: 30_000,
      });
    },
    [queryClient]
  );
}

/**
 * Query key prefix for invites queries.
 * Use with queryClient.invalidateQueries to invalidate all invite queries.
 */
export const PENDING_INVITES_QUERY_KEY_PREFIX = [SHIFT_INVITES_URL] as const;
